import React, { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  ViewDetailsButton,
  TableActionButton,
  TableActionsCell,
} from "../../common/adminTable";
import { list, search, blockUser, getUserById } from "./Service";
import useTableSearch from "../../../hooks/useTableSearch";
import {
  BookingDetailHeader,
  BookingDetailUserCard,
} from "../bookings/BookingDetailUi";
import { formatListingDateTime } from "../../../utils/formatListingDate";

const Users = () => {
    const queryClient = useQueryClient();
    const [currentPage, setCurrentPage] = useState(1);
    const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
        useTableSearch();
    const [showError, setShowError] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [showBlockDialog, setShowBlockDialog] = useState(false);
    const [showViewDialog, setShowViewDialog] = useState(false);
    const [viewUserId, setViewUserId] = useState(null);
    const [isUnblockAction, setIsUnblockAction] = useState(false);
    const [copyToast, setCopyToast] = useState(null);



    // React Query hooks
    const { data: usersData } = useQuery({
        queryKey: ["users", currentPage, activeSearch],
        queryFn: () =>
            activeSearch
                ? search({ search: activeSearch, page: currentPage })
                : list({ page: currentPage }),
        keepPreviousData: true,
    });

    const { data: viewUserData, isLoading: isLoadingUserDetails } = useQuery({
        queryKey: ["user", viewUserId],
        queryFn: () => getUserById(viewUserId),
        enabled: !!viewUserId,
    });

    // block/unblock user mutation
    const blockUserMutation = useMutation({
        mutationFn: ({ id, unblock }) => blockUser(id, unblock),
        onSuccess: (_, { unblock }) => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            showSuccessMessage(unblock ? "User unblocked successfully" : "User blocked successfully");
        },
        onError: (error) => {
            showErrorMessage(error.message || "Failed to update user status");
        },
    });

    const users = usersData?.data?.data || [];
    const selectedUser = viewUserData?.data;
    const pagination = {
        currentPage: usersData?.data?.current_page || 1,
        lastPage: usersData?.data?.last_page || 1,
        perPage: usersData?.data?.per_page || 10,
        total: usersData?.data?.total || 0,
        from: usersData?.data?.from || 0,
        to: usersData?.data?.to || 0,
    };

    // Helper functions
    const showSuccessMessage = (message) => {
        setSuccessMessage(message);
        setShowSuccess(true);
        setTimeout(() => {
            setShowSuccess(false);
            setSuccessMessage("");
        }, 3000);
    };

    const showErrorMessage = (errorMsg) => {
        setShowError(true);
        setTimeout(() => {
            setShowError(false);
        }, 3000);
    };

    const handlePageChange = (page) => {
        if (page !== currentPage) {
            setCurrentPage(page);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [activeSearch]);

    const handleBlockUserClick = (id, isBlocked) => {
        setSelectedUserId(id);
        setIsUnblockAction(isBlocked);
        setShowBlockDialog(true);
    };

    const confirmBlockAction = () => {
        if (selectedUserId) {
            blockUserMutation.mutate({
                id: selectedUserId,
                unblock: isUnblockAction ? 0 : 1
            });
            setShowBlockDialog(false);
            setSelectedUserId(null);
        }
    };

    const handleCloseViewDialog = useCallback(() => {
        setShowViewDialog(false);
        setViewUserId(null);
    }, []);

    const handleViewUser = (user) => {
        setViewUserId(user.id);
        setShowViewDialog(true);
    };

    const handleCopied = useCallback((label) => {
        setCopyToast({ message: `${label} copied to clipboard`, type: "success" });
        setTimeout(() => setCopyToast(null), 2500);
    }, []);

    return (
        <AdminPageLayout>
            {copyToast && (
                <Toster
                    message={copyToast.message}
                    type={copyToast.type}
                    duration={2500}
                    onClose={() => setCopyToast(null)}
                />
            )}

            {/* Error Toster */}
            {showError && (
                <Toster
                    message="An error occurred"
                    type="error"
                    duration={3000}
                    onClose={() => setShowError(false)}
                />
            )}

            {/* Success Toster */}
            {showSuccess && successMessage && (
                <Toster
                    message={successMessage}
                    type="success"
                    duration={3000}
                    onClose={() => {
                        setShowSuccess(false);
                        setSuccessMessage("");
                    }}
                />
            )}

            <AdminPageHeader
                title={ADMIN_PAGE_META.users.title}
                subtitle={ADMIN_PAGE_META.users.subtitle}
            />

            <AdminPageCard>
                <AdminPageCardToolbar>
                    <AdminTableToolbar className="mb-0">
                        <TableSearchInput
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by name, email, phone..."
                        />
                    </AdminTableToolbar>
                </AdminPageCardToolbar>

                <AdminTable
                    className="shadow-none rounded-none"
                    footer={
                        <AdminTableFooter>
                            <Pagination
                                pagination={pagination}
                                onPageChange={handlePageChange}
                                showInfo={true}
                            />
                        </AdminTableFooter>
                    }
                >
                <AdminTableHead>
                    <AdminTableTh>#</AdminTableTh>
                    <AdminTableTh>Name</AdminTableTh>
                    <AdminTableTh>Email</AdminTableTh>
                    <AdminTableTh>Phone</AdminTableTh>
                    <AdminTableTh>Status</AdminTableTh>
                    <AdminTableTh align="right">Actions</AdminTableTh>
                </AdminTableHead>
                <AdminTableBody>
                    {users.length > 0 ? (
                        users.map((user, index) => (
                            <AdminTableRow
                                key={user.id}
                                className={user.is_blocked ? "bg-red-50" : ""}
                            >
                                <AdminTableCell>{index + 1}</AdminTableCell>
                                <AdminTableCell>{user.name}</AdminTableCell>
                                <AdminTableCell>{user.email}</AdminTableCell>
                                <AdminTableCell>{user.phone}</AdminTableCell>
                                <AdminTableCell>
                                    <span
                                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            user.is_blocked
                                                ? "bg-red-100 text-red-800"
                                                : "bg-green-100 text-green-800"
                                        }`}
                                    >
                                        {user.is_blocked ? "Blocked" : "Active"}
                                    </span>
                                </AdminTableCell>
                                <TableActionsCell>
                                    <ViewDetailsButton
                                        label="View"
                                        onClick={() => handleViewUser(user)}
                                        ariaLabel={`View details for ${user.name}`}
                                    />
                                    <TableActionButton
                                        variant={user.is_blocked ? "unblock" : "block"}
                                        showLabel={false}
                                        onClick={() => handleBlockUserClick(user.id, user.is_blocked)}
                                        ariaLabel={
                                            user.is_blocked
                                                ? `Unblock ${user.name}`
                                                : `Block ${user.name}`
                                        }
                                    />
                                </TableActionsCell>
                            </AdminTableRow>
                        ))
                    ) : (
                        <TableEmptyRow
                            colSpan={6}
                            searchQuery={activeSearch}
                            emptyMessage="No users found"
                            searchMessage="No users found matching your search"
                            onClearSearch={clearSearch}
                        />
                    )}
                </AdminTableBody>
            </AdminTable>
            </AdminPageCard>

            {/* Block/Unblock Dialog */}
            {showBlockDialog && (
                <Dialog
                    title={isUnblockAction ? "Unblock User" : "Block User"}
                    open={showBlockDialog}
                    onClose={() => {
                        setShowBlockDialog(false);
                        setSelectedUserId(null);
                    }}
                    primaryButtonText={isUnblockAction ? "Unblock User" : "Block User"}
                    primaryButtonAction={confirmBlockAction}
                >
                    <div>
                        <h1>
                            {isUnblockAction
                                ? "Would you like to unblock this user?"
                                : "Would you like to block this user?"}
                        </h1>
                    </div>
                </Dialog>
            )}

            <Dialog
                title="User Details"
                open={showViewDialog}
                onClose={handleCloseViewDialog}
                width="w-full"
                variant="detail"
                maxWidth="2xl"
                showCloseButton={false}
            >
                {!showViewDialog || isLoadingUserDetails || !selectedUser ? (
                    <div className="flex justify-center items-center p-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : (
                    <div className="space-y-5">
                        <BookingDetailHeader
                            bookingId={selectedUser.id}
                            idLabel="User"
                            title={selectedUser.name}
                            status={selectedUser.is_blocked ? "Blocked" : "Active"}
                            statusClassName={
                                selectedUser.is_blocked
                                    ? "bg-red-50 text-red-700 ring-red-600/20"
                                    : "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                            }
                            bookedAt={formatListingDateTime(selectedUser.created_at)}
                            bookedOnLabel="Registered on"
                            onCopyBookingId={handleCopied}
                        />
                        <BookingDetailUserCard
                            user={selectedUser}
                            onCopied={handleCopied}
                        />
                    </div>
                )}
            </Dialog>

        </AdminPageLayout>
    );
};

export default Users;
